import { Badge, Button } from '@repo/ui'
import { Cable, FileText } from 'lucide-react'
import { connectorLabel, connectorStateLabel, listConnectors } from '@/api/content/connector'
import { fetchRagFiles } from '@/api/content/file'
import { fetchRagTexts } from '@/api/content/text'
import { processingDetailsDisplay } from '@/components/processingDetails'
import type { ProcessingDetails } from '@/api/content/connector'
import { PanelFailure, PanelHeader } from './PanelChrome'
import { usePanelArtifact } from './usePanelArtifact'
import type { ReactElement, ReactNode } from 'react'

// What a content canvas holds. Each shape is the panel's own rather than the wire item's:
// the file and text searches answer with creator columns, page counts and a tag list the
// panel does not need all of, and a panel that names its fields cannot quietly start
// rendering a new one the day the search grows another.
export type ContentFile = {
  fileId: number
  fileName: string
  description: string
  fileUploadState: string
  processingDetails: string
  updatedAtTsMs: number
  updatedByFirstName: string
  updatedBySurname: string
  tags: Array<{ tagId: number; name: string; colour: string }>
}

export type ContentText = {
  docId: number
  text: string
  updatedAtTsMs: number
  updatedByFirstName: string
  updatedBySurname: string
  tags: Array<{ tagId: number; name: string; colour: string }>
}

export type ContentConnector = {
  connectorId: number
  connectorType: string
  connectorDescription: string
  state: string
  statusDetails: string
  lastSyncedAtTsMs?: number
  updatedAtTsMs: number
}

// Where a panel's management action goes. Files and texts share a docId-shaped filter, so
// the target also names the kind. Scope selects the organisation or workspace settings
// dialog. Connectors belong to the workspace, and their id is optional when opening the
// full connector list.
export type ContentPanelTarget =
  | { page: 'file'; docId: number; scope: 'workspace' | 'global' }
  | { page: 'text'; docId: number; scope: 'workspace' | 'global' }
  | { page: 'connector'; connectorId?: number }
  | { page: 'content' }

export type ContentPanelServices = {
  loadFile: (projectId: number, fileId: number, scope: 'workspace' | 'global') => Promise<ContentFile>
  loadText: (projectId: number, docId: number, scope: 'workspace' | 'global') => Promise<ContentText>
  loadConnector: (projectId: number, connectorId: number) => Promise<ContentConnector>
}

// The organisation's corpus is what the search answers with when it is asked for no
// workspace at all, so a global read leaves the filter off rather than sending a value for
// it. The loaders still take a projectId at both scopes: the caller always has one, and a
// signature that changed shape with the scope would put that branch on every call site.
function contentScopeFilter(projectId: number, scope: 'workspace' | 'global'): { projectId?: number } {
  return scope === 'global' ? {} : { projectId }
}

// The paged search is the only read there is for a single file or text: both take a docId
// filter and answer with a page of items. Exactly one item carrying the asked-for id is
// the only answer that means "the asked-for corpus holds this one", so anything else is an
// identity mismatch rather than an empty panel, exactly as the document canvas treats a
// document whose projectId is not the one on screen.
export async function loadContentFile(
  projectId: number,
  fileId: number,
  scope: 'workspace' | 'global'
): Promise<ContentFile> {
  const response = await fetchRagFiles({ ...contentScopeFilter(projectId, scope), docId: fileId, currentPage: 1 })
  const item = response.items.length === 1 ? response.items[0] : undefined
  if (item === undefined || item.fileId !== fileId) throw new Error('The file identity did not match this workspace.')
  return {
    fileId: item.fileId,
    fileName: item.fileName,
    description: item.description,
    fileUploadState: item.fileUploadState,
    processingDetails: item.processingDetails,
    updatedAtTsMs: item.updatedAtTsMs,
    updatedByFirstName: item.updatedByFirstName,
    updatedBySurname: item.updatedBySurname,
    tags: item.tags.map((tag) => ({ tagId: tag.tagId, name: tag.name, colour: tag.colour }))
  }
}

export async function loadContentText(
  projectId: number,
  docId: number,
  scope: 'workspace' | 'global'
): Promise<ContentText> {
  const response = await fetchRagTexts({ ...contentScopeFilter(projectId, scope), docId, currentPage: 1 })
  const item = response.items.length === 1 ? response.items[0] : undefined
  if (item === undefined || item.docId !== docId) throw new Error('The text identity did not match this workspace.')
  return {
    docId: item.docId,
    text: item.text,
    updatedAtTsMs: item.updatedAtTsMs,
    updatedByFirstName: item.updatedByFirstName,
    updatedBySurname: item.updatedBySurname,
    tags: item.tags.map((tag) => ({ tagId: tag.tagId, name: tag.name, colour: tag.colour }))
  }
}

// Connectors have no single read either, so the panel picks its one out of the workspace's
// list. The server already scopes that list: GetContentConnectors matches project_id to the
// workspace asked for, or null to null, so a well-formed answer cannot hold another
// workspace's connector and this check cannot fire against one today. It is made anyway,
// because the panel is addressed by a URL a reader can edit and what it paints should not
// rest on that filter staying exactly what it is. The same line rejects an organisation-level
// connector, which the null branch of that filter returns carrying no projectId at all.
export async function loadContentConnector(projectId: number, connectorId: number): Promise<ContentConnector> {
  const response = await listConnectors(projectId)
  const connector = response.connectors.find((candidate) => candidate.id === connectorId)
  if (connector === undefined || connector.projectId !== projectId) {
    throw new Error('The connector identity did not match this workspace.')
  }
  return {
    connectorId: connector.id,
    connectorType: connector.connectorType,
    connectorDescription: connector.connectorDescription,
    state: connector.state,
    statusDetails: connector.statusDetails,
    ...(connector.lastSyncedAtTsMs === undefined ? {} : { lastSyncedAtTsMs: connector.lastSyncedAtTsMs }),
    updatedAtTsMs: connector.updatedAtTsMs
  }
}

function formatContentTime(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(timestamp)
}

function updatedByName(firstName: string, surname: string): string {
  return `${firstName} ${surname}`.trim()
}

function ContentDetail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  )
}

// Names only. The tag's colour is carried through the load for whoever needs it, and no
// surface in the app has decided yet what a tag colour looks like on screen.
function ContentTags({ tags }: { tags: ContentFile['tags'] }) {
  if (tags.length === 0) return null
  return (
    <div className="mt-5 flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <Badge key={tag.tagId} variant="secondary">
          {tag.name}
        </Badge>
      ))}
    </div>
  )
}

// One action, and it is a handover rather than an edit. Scoped settings owns mutations so
// the canvas remains a focused read surface.
function ManageAction({ onOpen }: { onOpen: () => void }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onOpen}>
      Manage in settings
    </Button>
  )
}

export function FilePanel({
  projectId,
  fileId,
  contentScope,
  scope,
  services,
  onOpenContentPage
}: {
  projectId: number
  fileId: number
  // Which corpus the id belongs to. It is not the `scope` beside it: that one is the line
  // of text under the title saying whose workspace this is, and this one is part of what
  // is being read.
  contentScope: 'workspace' | 'global'
  scope: string
  services: ContentPanelServices
  onOpenContentPage?: (target: ContentPanelTarget) => void
}): ReactElement {
  // A content row has no revision mark to follow, so the identity and the retain key are
  // the same string: nothing but the canvas moving to another file starts a fresh read.
  // The scope is in it because it is part of the request, not because two rows could share
  // an id: ids are unique across both corpora, so the same id at the other scope finds
  // nothing rather than another file. Finding nothing is the point. The panel has to run
  // that read and report that the file could not be loaded, rather than go on painting the
  // copy the previous address left behind.
  const identity = `${projectId}:${contentScope}:${fileId}`
  const {
    artifact: file,
    failed,
    reload
  } = usePanelArtifact<ContentFile>({
    identity,
    retainKey: identity,
    enabled: true,
    load: () => services.loadFile(projectId, fileId, contentScope)
  })
  if (file === null) {
    if (failed) return <PanelFailure message="This file could not be loaded." retry={reload} />
    return <p className="px-5 py-8 text-sm text-muted-foreground">Loading file</p>
  }
  const details = processingDetailsDisplay(file.processingDetails as ProcessingDetails)
  return (
    <div data-testid="content-file-canvas">
      <PanelHeader
        title={file.fileName}
        icon={<FileText className="size-4 text-primary" aria-hidden="true" />}
        scope={scope}
        {...(onOpenContentPage === undefined
          ? {}
          : {
              actions: (
                <ManageAction onOpen={() => onOpenContentPage({ page: 'file', docId: fileId, scope: contentScope })} />
              )
            })}
      />
      <div className="px-5 py-6 text-sm">
        <p className="wrap-anywhere whitespace-normal">{file.description || 'No description'}</p>
        <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
          <ContentDetail label="State">
            <span className="capitalize">{file.fileUploadState.replaceAll('_', ' ')}</span>
          </ContentDetail>
          {details !== null && (
            <ContentDetail label="Processing">
              <span className={details.colourClass}>{details.label}</span>
            </ContentDetail>
          )}
          <ContentDetail label="Last updated">
            {formatContentTime(file.updatedAtTsMs)} by {updatedByName(file.updatedByFirstName, file.updatedBySurname)}
          </ContentDetail>
        </dl>
        <ContentTags tags={file.tags} />
      </div>
    </div>
  )
}

export function TextPanel({
  projectId,
  docId,
  contentScope,
  scope,
  services,
  onOpenContentPage
}: {
  projectId: number
  docId: number
  contentScope: 'workspace' | 'global'
  scope: string
  services: ContentPanelServices
  onOpenContentPage?: (target: ContentPanelTarget) => void
}): ReactElement {
  const identity = `${projectId}:${contentScope}:${docId}`
  const {
    artifact: text,
    failed,
    reload
  } = usePanelArtifact<ContentText>({
    identity,
    retainKey: identity,
    enabled: true,
    load: () => services.loadText(projectId, docId, contentScope)
  })
  if (text === null) {
    if (failed) return <PanelFailure message="This text could not be loaded." retry={reload} />
    return <p className="px-5 py-8 text-sm text-muted-foreground">Loading text</p>
  }
  return (
    <div data-testid="content-text-canvas">
      {/* A stored text has no name of its own, and the first line is already the first
          thing below the header, so the title says what kind of thing this is and leaves
          the words to the body. */}
      <PanelHeader
        title="Text"
        icon={<FileText className="size-4 text-primary" aria-hidden="true" />}
        scope={scope}
        {...(onOpenContentPage === undefined
          ? {}
          : {
              actions: <ManageAction onOpen={() => onOpenContentPage({ page: 'text', docId, scope: contentScope })} />
            })}
      />
      <div className="px-5 py-6 text-sm">
        <p className="wrap-anywhere whitespace-pre-wrap">{text.text}</p>
        <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
          <ContentDetail label="Last updated">
            {formatContentTime(text.updatedAtTsMs)} by {updatedByName(text.updatedByFirstName, text.updatedBySurname)}
          </ContentDetail>
        </dl>
        <ContentTags tags={text.tags} />
      </div>
    </div>
  )
}

export function ConnectorPanel({
  projectId,
  connectorId,
  scope,
  services,
  onOpenContentPage
}: {
  projectId: number
  connectorId: number
  scope: string
  services: ContentPanelServices
  onOpenContentPage?: (target: ContentPanelTarget) => void
}): ReactElement {
  const identity = `${projectId}:${connectorId}`
  const {
    artifact: connector,
    failed,
    reload
  } = usePanelArtifact<ContentConnector>({
    identity,
    retainKey: identity,
    enabled: true,
    load: () => services.loadConnector(projectId, connectorId)
  })
  if (connector === null) {
    if (failed) return <PanelFailure message="This connector could not be loaded." retry={reload} />
    return <p className="px-5 py-8 text-sm text-muted-foreground">Loading connector</p>
  }
  const stateLabel = connectorStateLabel(connector.state, connector.statusDetails)
  return (
    <div data-testid="content-connector-canvas">
      <PanelHeader
        title={connectorLabel(connector.connectorType)}
        icon={<Cable className="size-4 text-primary" aria-hidden="true" />}
        scope={scope}
        {...(onOpenContentPage === undefined
          ? {}
          : { actions: <ManageAction onOpen={() => onOpenContentPage({ page: 'connector', connectorId })} /> })}
      />
      <div className="px-5 py-6 text-sm">
        <p className="wrap-anywhere whitespace-normal">{connector.connectorDescription || 'No description'}</p>
        <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
          <ContentDetail label="State">
            <span className={stateLabel.colorClass}>{stateLabel.label}</span>
            {stateLabel.message !== undefined && <p className="mt-1 text-muted-foreground">{stateLabel.message}</p>}
          </ContentDetail>
          <ContentDetail label="Last synced">
            {connector.lastSyncedAtTsMs === undefined ? 'Never' : formatContentTime(connector.lastSyncedAtTsMs)}
          </ContentDetail>
          <ContentDetail label="Last updated">{formatContentTime(connector.updatedAtTsMs)}</ContentDetail>
        </dl>
      </div>
    </div>
  )
}
